/**
 * SubtitlesContext
 *
 * Provides subtitle-only state and API actions to the component tree,
 * eliminating prop drilling through all section components.
 * Mirrors the DubbingContext pattern.
 */

import React, { useMemo, useCallback } from 'react';
import { useSubtitlesState } from './hooks/useSubtitlesState';
import { useSubtitlesApi } from './hooks/useSubtitlesApi';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import type { SubtitleOutputMode, SubtitleFormat, SubtitleStyle } from './types';
import type { MediaType } from '@/components/common/MediaUploader';
import type { UploadAndGenerateOptions } from './hooks/useSubtitlesApi';
import { SubtitlesContext, type SubtitlesContextValue } from './SubtitlesContext.context';

// ============================================================================
// Provider
// ============================================================================

interface SubtitlesProviderProps {
  children: React.ReactNode;
}

export function SubtitlesProvider({ children }: SubtitlesProviderProps): JSX.Element {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { state, actions, isUploading, isGenerating, isCompleted, showForm } = useSubtitlesState();

  const onSuccess = useCallback(
    (_titleKey: string, _messageKey: string) => {
      showToast(
        'success',
        t('subtitles.result.completedTitle', { defaultValue: 'Subtitles Ready' }),
        t('subtitles.result.completedMessage', {
          defaultValue: 'Your subtitle files are ready to download.',
        })
      );
    },
    [showToast, t]
  );

  const onError = useCallback(
    (_titleKey: string, message: string) => {
      showToast(
        'error',
        t('subtitles.errors.failedTitle', { defaultValue: 'Generation Failed' }),
        message
      );
    },
    [showToast, t]
  );

  const { uploadAndGenerate, cancelJob, getDownloadUrl } = useSubtitlesApi({
    actions,
    state,
    onSuccess,
    onError,
  });

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------

  const handleMediaSelect = useCallback(
    (file: File, type: MediaType) => {
      actions.setMedia(file, type);
    },
    [actions]
  );

  const handleMediaRemove = useCallback(() => {
    actions.clearMedia();
  }, [actions]);

  const handleSourceLanguageChange = useCallback(
    (language: string) => {
      actions.setSourceLanguage(language);
    },
    [actions]
  );

  const handleTargetLanguagesChange = useCallback(
    (languages: string[]) => {
      actions.setTargetLanguages(languages);
    },
    [actions]
  );

  const handleOutputModeChange = useCallback(
    (mode: SubtitleOutputMode) => {
      actions.setOutputMode(mode);
    },
    [actions]
  );

  const handleFormatsChange = useCallback(
    (formats: SubtitleFormat[]) => {
      actions.setFormats(formats);
    },
    [actions]
  );

  const handleLlmRefinementChange = useCallback(
    (enabled: boolean) => {
      actions.setLlmRefinement(enabled);
    },
    [actions]
  );

  const handleKaraokeChange = useCallback(
    (enabled: boolean) => {
      actions.setKaraoke(enabled);
    },
    [actions]
  );

  const handleStyleChange = useCallback(
    (style: SubtitleStyle) => {
      actions.setStyle(style);
    },
    [actions]
  );

  const handleHardBurnConfirmedChange = useCallback(
    (confirmed: boolean) => {
      actions.setHardBurnConfirmed(confirmed);
    },
    [actions]
  );

  const handleGenerate = useCallback(async () => {
    if (!state.mediaFile) {
      showToast(
        'error',
        t('subtitles.errors.noMediaTitle', { defaultValue: 'No File Selected' }),
        t('subtitles.errors.noMediaMessage', {
          defaultValue: 'Please upload a video or audio file first.',
        })
      );
      return;
    }

    if (state.targetLanguages.length === 0) {
      showToast(
        'error',
        t('subtitles.errors.noLanguagesTitle', { defaultValue: 'No Languages Selected' }),
        t('subtitles.errors.noLanguagesMessage', {
          defaultValue: 'Please select at least one target language.',
        })
      );
      return;
    }

    // Frontend gate: hard_burn requires confirmed cost
    if (state.outputMode === 'hard_burn' && !state.hardBurnConfirmed) {
      showToast(
        'error',
        t('subtitles.hardBurn.confirmRequired', { defaultValue: 'Confirmation Required' }),
        t('subtitles.hardBurn.confirmRequiredMessage', {
          defaultValue:
            'Please check the cost confirmation checkbox before generating a hard burn-in video.',
        })
      );
      return;
    }

    const options: UploadAndGenerateOptions = {
      file: state.mediaFile,
      sourceLanguage: state.sourceLanguage,
      targetLanguages: state.targetLanguages,
      outputMode: state.outputMode,
      formats: state.formats,
      useLlmRefinement: state.useLlmRefinement,
      useKaraoke: state.useKaraoke,
      confirmCost: state.hardBurnConfirmed,
      style: state.outputMode === 'hard_burn' ? state.style : undefined,
    };

    await uploadAndGenerate(options);
  }, [state, uploadAndGenerate, showToast, t]);

  const handleCancel = useCallback(async () => {
    await cancelJob();
  }, [cancelJob]);

  const handleReset = useCallback(() => {
    actions.reset();
  }, [actions]);

  // ------------------------------------------------------------------
  // Context value (memoised)
  // ------------------------------------------------------------------

  const value = useMemo<SubtitlesContextValue>(
    () => ({
      state,
      actions,
      isUploading,
      isGenerating,
      isCompleted,
      showForm,
      handleMediaSelect,
      handleMediaRemove,
      handleSourceLanguageChange,
      handleTargetLanguagesChange,
      handleOutputModeChange,
      handleFormatsChange,
      handleLlmRefinementChange,
      handleKaraokeChange,
      handleStyleChange,
      handleHardBurnConfirmedChange,
      handleGenerate,
      handleCancel,
      handleReset,
      getDownloadUrl,
    }),
    [
      state,
      actions,
      isUploading,
      isGenerating,
      isCompleted,
      showForm,
      handleMediaSelect,
      handleMediaRemove,
      handleSourceLanguageChange,
      handleTargetLanguagesChange,
      handleOutputModeChange,
      handleFormatsChange,
      handleLlmRefinementChange,
      handleKaraokeChange,
      handleStyleChange,
      handleHardBurnConfirmedChange,
      handleGenerate,
      handleCancel,
      handleReset,
      getDownloadUrl,
    ]
  );

  return <SubtitlesContext.Provider value={value}>{children}</SubtitlesContext.Provider>;
}

// ============================================================================
// Consumer hook
// ============================================================================
